import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errMsg } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { RatingBadge } from "@/components/Rating";

const EMPTY = { name: "", email: "", password: "", phone: "", services: [], service_areas: "", years_experience: 0, languages: "", availability: "", bio: "", checks: "" };
const L = ({ l, children }) => <div className="space-y-1"><Label className="text-xs">{l}</Label>{children}</div>;
const split = (s) => (Array.isArray(s) ? s : s.split(",").map((x) => x.trim()).filter(Boolean));

function ContractorForm({ initial, onSaved, onClose }) {
  const { t } = useI18n();
  const isNew = !initial.id;
  const [f, setF] = useState({ ...EMPTY, ...initial, service_areas: (initial.service_areas || []).join?.(", ") ?? "", languages: (initial.languages || []).join?.(", ") ?? "", checks: (initial.checks || []).join?.(", ") ?? "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, service_areas: split(f.service_areas), languages: split(f.languages), checks: split(f.checks), years_experience: Number(f.years_experience || 0) };
    try {
      if (isNew) await api.post("/owner/contractors", body); else await api.put(`/owner/contractors/${f.id}`, body);
      toast.success(t("o.saved")); onSaved(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="contractor-form">
      <L l={t("o.name")}><Input value={f.name} onChange={set("name")} data-testid="cf-name" /></L>
      <L l={t("o.phone_wa")}><Input value={f.phone} onChange={set("phone")} data-testid="cf-phone" /></L>
      {isNew && <L l={t("o.login_email")}><Input value={f.email} onChange={set("email")} data-testid="cf-email" /></L>}
      {isNew && <L l={t("o.temp_pw")}><Input value={f.password} onChange={set("password")} data-testid="cf-password" /></L>}
      <div className="sm:col-span-2">
        <Label className="text-xs">{t("o.approved_services")}</Label>
        <div className="grid grid-cols-2 gap-1 mt-1">
          {SERVICES.map(({ key }) => (
            <label key={key} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.services.includes(key)} data-testid={`cf-service-${key}`}
              onChange={() => setF({ ...f, services: f.services.includes(key) ? f.services.filter((x) => x !== key) : [...f.services, key] })} />{t(`svc.${key}`)}</label>
          ))}
        </div>
      </div>
      <div className="sm:col-span-2"><L l={t("o.areas_long")}><Input value={f.service_areas} onChange={set("service_areas")} data-testid="cf-areas" /></L></div>
      <L l={t("o.years")}><Input type="number" value={f.years_experience} onChange={set("years_experience")} data-testid="cf-years" /></L>
      <L l={t("o.languages")}><Input value={f.languages} onChange={set("languages")} data-testid="cf-languages" /></L>
      <L l={t("o.availability")}><Input value={f.availability} onChange={set("availability")} data-testid="cf-availability" /></L>
      <L l={t("o.checks_eg")}><Input value={f.checks} onChange={set("checks")} data-testid="cf-checks" /></L>
      <div className="sm:col-span-2"><L l={t("o.bio")}><Input value={f.bio} onChange={set("bio")} data-testid="cf-bio" /></L></div>
      <Button className="sm:col-span-2 bg-teal-600 hover:bg-teal-700" onClick={save} data-testid="cf-save-btn">{t("o.save")}</Button>
    </div>
  );
}

const TONE = { active: "bg-emerald-100 text-emerald-800", inactive: "bg-amber-100 text-amber-800", removed: "bg-slate-200 text-slate-600" };

export default function OwnerContractors() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/owner/contractors").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (c, status) => {
    if (status === "removed" && !window.confirm(t("o.remove_confirm", { name: c.name }))) return;
    try { await api.post(`/owner/contractors/${c.id}/status`, { status }); toast.success(t("o.c_status_done")); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="owner-contractors">
      <Button onClick={() => setEdit({})} className="rounded-full bg-teal-600 hover:bg-teal-700" data-testid="add-contractor-btn"><Plus className="h-4 w-4 me-1" />{t("o.add_contractor")}</Button>
      <div className="mt-4 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`owner-contractor-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div><p className="font-semibold">{c.name}</p><RatingBadge c={c} /><p className="text-xs text-slate-500">{c.email} · {c.phone}</p></div>
              <span className={`text-xs rounded-full px-2 py-0.5 font-semibold ${TONE[c.status]}`} data-testid="contractor-status">{t(`o.s_${c.status}`)}</span>
            </div>
            <p className="text-xs text-slate-600 mt-3">{c.services.map((s) => t(`svc.${s}`)).join(" · ")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("o.areas_label")}: {c.service_areas.join(", ")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("o.completed_active", { c: c.jobs_completed, a: c.active_jobs })}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setEdit(c)} data-testid={`edit-contractor-${c.id}`}><Pencil className="h-3.5 w-3.5 me-1" />{t("o.edit")}</Button>
              {c.status !== "active" && <Button size="sm" variant="outline" onClick={() => setStatus(c, "active")} data-testid={`activate-contractor-${c.id}`}>{t("o.activate")}</Button>}
              {c.status === "active" && <Button size="sm" variant="outline" onClick={() => setStatus(c, "inactive")} data-testid={`deactivate-contractor-${c.id}`}>{t("o.deactivate")}</Button>}
              {c.status !== "removed" && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setStatus(c, "removed")} data-testid={`remove-contractor-${c.id}`}>{t("o.remove")}</Button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t(edit?.id ? "o.edit_contractor" : "o.add_contractor")}</DialogTitle></DialogHeader>
          {edit && <ContractorForm initial={edit} onSaved={load} onClose={() => setEdit(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
